/**
 * Cut lines on an axis: the one primitive the grading model repeats at every scale. Lines over a
 * group's score give a verdict; lines over an accumulator's running total give a target and
 * tiers. Zero lines means the raw number is shown, one line is pass / fail, more give tiers.
 */

import { GradingConfigError } from './types';

export interface CutLines {
  /** Line positions, ascending. */
  readonly lines: readonly number[];
  /** One label per bucket, lowest bucket first: `lines.length + 1` labels (none when no lines). */
  readonly labels: readonly string[];
  /**
   * Which bucket a value exactly on a line falls into: `upper` (at or above the line passes,
   * e.g. ">= 95") or `lower` (at or below, e.g. "<= 1 error"). Default `upper`.
   */
  readonly atLine?: 'upper' | 'lower';
}

export function cutLinesErrors(cut: CutLines): string[] {
  const ascending = cut.lines.every((line, index) => index === 0 || line > cut.lines[index - 1]);
  const finite = cut.lines.every((line) => Number.isFinite(line));
  const labelCount =
    cut.lines.length === 0 ? cut.labels.length <= 1 : cut.labels.length === cut.lines.length + 1;
  return [
    ...(ascending && finite ? [] : ['cut lines must be finite and strictly ascending']),
    ...(labelCount ? [] : ['cut lines need exactly one label per bucket (lines + 1)']),
  ];
}

export function validateCutLines(cut: CutLines, label = 'cut lines'): void {
  const errors = cutLinesErrors(cut);
  if (errors.length > 0) {
    throw new GradingConfigError(`${label}: ${errors.join('; ')}`);
  }
}

/** The bucket a value falls into, 0 = lowest. */
export function bucketOf(cut: CutLines, value: number): number {
  const atLine = cut.atLine ?? 'upper';
  return cut.lines.filter((line) => (atLine === 'upper' ? value >= line : value > line)).length;
}

/** The label a value earns; null with zero lines (the raw number is the reading). */
export function readCutLines(cut: CutLines, value: number): string | null {
  if (cut.lines.length === 0) {
    return null;
  }
  return cut.labels[bucketOf(cut, value)];
}
